<script lang="ts" setup>
  import { Badge, type BadgeVariants } from '~/components/ui/badge'
  import CircleSpinner from '~/components/CircleSpinner.vue'

  type DisplayStatus = 'done' | 'processing' | 'error'

  const props = withDefaults(
    defineProps<{
      status?: string
      error?: string | null
      progress?: number
    }>(),
    {
      status: undefined,
      error: undefined,
      progress: undefined
    }
  )

  const { t, n } = useI18n()

  const rawStatus = computed(() => props.status ?? '')
  const errorMessage = computed(() => props.error ?? null)

  const displayStatus = computed<DisplayStatus>(() => {
    if (errorMessage.value) return 'error'
    if (rawStatus.value === 'done') return 'done'
    if (rawStatus.value === 'error' || rawStatus.value === 'failed') return 'error'
    return 'processing'
  })

  const VARIANTS: Record<DisplayStatus, BadgeVariants['variant']> = {
    done: 'success',
    processing: 'pending',
    error: 'destructive'
  }

  const label = computed(() => {
    if (displayStatus.value === 'processing' && (rawStatus.value === 'queued' || rawStatus.value === 'running')) {
      return t(`reports.downloads.status.${rawStatus.value}`)
    }
    return t(`reports.generation.status.${displayStatus.value}`)
  })
</script>

<template>
  <div class="flex min-w-0 flex-col gap-0.5">
    <Badge :variant="VARIANTS[displayStatus]" class="h-5 w-fit whitespace-nowrap px-1.5 py-0 text-xs font-medium">
      <CircleSpinner v-if="displayStatus === 'processing'" class="mr-1 !size-3" />
      {{ label }}
      <span v-if="displayStatus === 'processing' && progress" class="ml-1 font-normal">
        {{ t('reports.downloads.rows', { count: n(progress) }, progress) }}
      </span>
    </Badge>
    <p v-if="errorMessage" :title="errorMessage" class="max-w-[28rem] truncate text-xs text-destructive">
      {{ errorMessage }}
    </p>
  </div>
</template>
